import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  cleanupUserSettings,
  closeProfileModal,
  openProfileModal,
} from "../../../store/slice/auth-slice";
import { getLocationName } from "../../../utils/windowname";
import { getAllChildren } from "../../../utils/routes_childs";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { FiLogOut } from "react-icons/fi";
import { RiFullscreenFill } from "react-icons/ri";
import ProfileModal from "../../../pages/Profile";
import Hamburger from "../../icons/Hamburger";
import ProfileIcon_mob from "../../icons/ProfileIcon_mob";
import LogoutIcon from "../../icons/Logout";
import PopupImages from "../popup";
import routes from "../../../routes";
import { BiSolidBell } from "react-icons/bi";
import { FaUserLarge } from "react-icons/fa6";
import {
  clearAllUnReadNotification,
  getUserNotifications,
  updateUnReadNotification,
} from "../../../api/auth/user";
import dayjs from "dayjs";
import Close from "../../icons/Close";
import { baseURL } from "../../../config/axios/interceptor";
import { IoMdRefresh } from "react-icons/io";

// Add CSS for the progress bar animation
const progressBarStyles = `
  @keyframes shrink {
    from { width: 100%; }
    to { width: 0%; }
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = progressBarStyles;
  document.head.appendChild(styleSheet);
}

// Notification Toast Component
const NotificationToast = ({ notification, onClose, onClick }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 9000); // Auto close after 9 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-5 md:left-1/2 transform -translate-x-1/2 z-[9999] md:max-w-sm w-full md:mx-4 animate-in slide-in-from-top-2 duration-300">
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => {
          onClick(notification, true);
          onClose();
        }}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <BiSolidBell className="w-4 h-4 text-pink" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  New Notification
                </p>
                <p className="text-sm text-gray-600 line-clamp-2 break-words">
                  {notification?.description}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {dayjs(notification?.createdAt).format("MMM DD, hh:mm a")}
                </p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  onClick(notification, false);
                }}
                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification Toast Manager Hook
const useNotificationToast = () => {
  const [notifications, setNotifications] = useState([]);
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [isShowingToast, setIsShowingToast] = useState(false);

  const showNotificationToast = useCallback(
    (notification) => {
      const id = Date.now();
      const notificationWithId = { ...notification, id };

      if (isShowingToast) {
        // If already showing a toast, add to queue
        setNotificationQueue((prev) => [...prev, notificationWithId]);
      } else {
        // Show immediately
        setNotifications([notificationWithId]);
        setIsShowingToast(true);
      }
    },
    [isShowingToast]
  );

  const removeNotificationToast = useCallback((id) => {
    setNotifications((prev) => {
      const filtered = prev.filter((notification) => notification.id !== id);

      // If we just removed the last notification and there are queued ones
      if (filtered.length === 0) {
        setIsShowingToast(false);

        // Show next notification from queue after a short delay
        setTimeout(() => {
          setNotificationQueue((queue) => {
            if (queue.length > 0) {
              const [nextNotification, ...remainingQueue] = queue;
              setNotifications([nextNotification]);
              setIsShowingToast(true);
              return remainingQueue;
            }
            return queue;
          });
        }, 300); // Small delay for smooth transition
      }

      return filtered;
    });
  }, []);

  // Enhanced function to show multiple unread notifications
  const showUnreadNotifications = useCallback((unreadNotifications) => {
    if (unreadNotifications.length === 0) return;

    const [firstNotification, ...remainingNotifications] = unreadNotifications;

    // Show first notification immediately
    const firstId = Date.now();
    setNotifications([{ ...firstNotification, id: firstId }]);
    setIsShowingToast(true);

    // Queue the rest
    const queuedNotifications = remainingNotifications.map(
      (notification, index) => ({
        ...notification,
        id: firstId + index + 1,
      })
    );

    setNotificationQueue(queuedNotifications);
  }, []);

  const NotificationToastContainer = ({ onNotificationClick }) => (
    <>
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClose={() => removeNotificationToast(notification.id)}
          onClick={onNotificationClick}
        />
      ))}
    </>
  );

  return {
    showNotificationToast,
    showUnreadNotifications,
    NotificationToastContainer,
    queueLength: notificationQueue.length,
  };
};

// Notification Item Component
const NotificationItem = ({ notification, onClick, isRead }) => (
  <div
    className={`min-h-14 cursor-pointer border-b border-gray-200 hover:bg-gray-50 transition-colors ${
      isRead ? "opacity-60" : "bg-blue-50"
    }`}
    onClick={() => onClick(notification)}
  >
    <div className="px-5 py-3">
      <div className="font-medium text-sm text-gray-800 break-words leading-relaxed">
        {notification?.description}
      </div>
      <div className="flex justify-between items-center mt-2">
        <p className="text-xs text-gray-500">
          {dayjs(notification?.createdAt).format("DD MMM YYYY, hh:mm a")}
        </p>
        {!isRead && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
      </div>
    </div>
  </div>
);

// Notification Panel Component
const NotificationPanel = ({
  isOpen,
  onClose,
  notifications,
  loading,
  onNotificationClick,
  onClearAll,
  hasUnread,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute top-20 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 animate-in slide-in-from-right-2 duration-200"
    >
      <div className="max-h-96 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2  flex justify-between items-center rounded-t-lg">
          <h3 className="font-semibold text-gray-800">Notifications</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Close />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-[420px]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : notifications?.length === 0 ? (
            <div className="h-[350px] flex flex-col gap-5 justify-center items-center">
              <img
                src={"/dentist-day.png"}
                alt="icon"
                className="w-[100px] h-[100px]"
              />
              <div className=" font-gilroysemibold">No Notifications Yet</div>
            </div>
          ) : (
            notifications.map((notification, index) => (
              <NotificationItem
                key={notification._id || index}
                notification={notification}
                onClick={onNotificationClick}
                isRead={notification.isRead}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {notifications?.length > 0 && hasUnread && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 rounded-b-lg">
            <button
              onClick={onClearAll}
              className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium"
            >
              Mark all as read
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Header Component
const Header = ({ handleMobSideBar }) => {
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const currentUser = useSelector((state) => state.auth);
  const {
    showNotificationToast,
    showUnreadNotifications,
    NotificationToastContainer,
    queueLength,
  } = useNotificationToast();

  // State management
  const [showLogout, setShowLogout] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [notificationPanel, setNotificationPanel] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Refs
  const logoutButtonRef = useRef(null);
  const notificationIconRef = useRef(null);
  const socket = useRef(null);
  const prevHasUnreadRef = useRef(hasUnread);

  // Computed values
  const formattedPathname = useMemo(
    () => getLocationName(location),
    [location]
  );
  const additionalBackButtonPaths = useMemo(() => getAllChildren(), []);
  const showBackButton = useMemo(
    () =>
      pathname?.split("/").length > 2 ||
      additionalBackButtonPaths.includes(pathname),
    [pathname, additionalBackButtonPaths]
  );

  // Notification functions
  const checkUnreadNotifications = useCallback((notifications) => {
    return notifications?.some((notification) => !notification.isRead) || false;
  }, []);

  const getNotifications = useCallback(async () => {
    setNotificationLoading(true);
    try {
      const response = await getUserNotifications();
      setNotificationList(response?.data || []);
      setHasUnread(checkUnreadNotifications(response?.data));
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setNotificationLoading(false);
    }
  }, [checkUnreadNotifications]);

  const handleNotificationClick = useCallback(
    async (notification, isOpen) => {
      try {
        if (!notification.isRead) {
          await updateUnReadNotification(notification._id);

          const updatedNotifications = notificationList.map((item) =>
            item._id === notification._id ? { ...item, isRead: true } : item
          );

          setNotificationList(updatedNotifications);
          setHasUnread(checkUnreadNotifications(updatedNotifications));
        }

        setNotificationPanel(false);
        if (notification.url && isOpen) {
          window.location.href = notification.url;
        }
      } catch (error) {
        console.error("Error updating notification:", error);
      }
    },
    [notificationList, checkUnreadNotifications]
  );

  const handleClearAllUnread = useCallback(async () => {
    try {
      await clearAllUnReadNotification();
      await getNotifications();
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  }, [getNotifications]);

  // Remove the showPushNotification function completely

  // Other handlers
  const handleProfileToggle = useCallback(() => {
    dispatch(currentUser.isOpen ? closeProfileModal() : openProfileModal());
  }, [dispatch, currentUser.isOpen]);

  const handleLogout = useCallback(() => {
    dispatch(cleanupUserSettings());
    navigation("/login");
  }, [dispatch, navigation]);

  const handleBackNavigation = useCallback(() => {
    const parentRoute = routes.find(
      (route) =>
        route.childrens &&
        route.childrens.some((child) => pathname.startsWith(child))
    );

    if (parentRoute && parentRoute.childrens.length > 0) {
      navigation(parentRoute.path);
    } else {
      navigation(-1);
    }
  }, [pathname, navigation]);

  const handleFullScreenToggle = useCallback(() => {
    const element = document.documentElement;

    if (!isFullScreen) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }, [isFullScreen]);

  // Effects
  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  // Handle unread notifications change
  useEffect(() => {
    const prevHasUnread = prevHasUnreadRef.current;

    if (hasUnread && !prevHasUnread && notificationList.length > 0) {
      // Get all unread notifications
      const unreadNotifications = notificationList.filter(
        (notification) => !notification.isRead
      );

      if (unreadNotifications.length > 0) {
        // Show all unread notifications in sequence
        showUnreadNotifications(unreadNotifications);
      }
    }

    prevHasUnreadRef.current = hasUnread;
  }, [hasUnread, notificationList, showUnreadNotifications]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        !!(
          document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        )
      );
    };

    const events = [
      "fullscreenchange",
      "mozfullscreenchange",
      "webkitfullscreenchange",
      "msfullscreenchange",
    ];
    events.forEach((event) =>
      document.addEventListener(event, handleFullScreenChange)
    );

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, handleFullScreenChange)
      );
    };
  }, []);

  // Click outside handler for logout
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        logoutButtonRef.current &&
        !logoutButtonRef.current.contains(event.target)
      ) {
        setShowLogout(false);
      }
    };

    if (showLogout) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showLogout]);

  // Socket connection
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!isConnected && !socket.current && token) {
      try {
        socket.current = new WebSocket(`${baseURL}?token=${token}`);

        socket.current.onopen = () => {
          setIsConnected(true);
          console.log("Socket connected");
        };

        socket.current.onclose = () => {
          setIsConnected(false);
          console.log("Socket disconnected");
        };

        socket.current.onerror = (error) => {
          console.error("Socket error:", error);
        };
      } catch (error) {
        console.error("Socket connection error:", error);
      }
    }

    return () => {
      if (socket.current) {
        socket.current.close();
        socket.current = null;
      }
    };
  }, [isConnected]);

  // Socket message handler
  useEffect(() => {
    if (isConnected && socket.current) {
      socket.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("Socket message:", data);

          if (data?.type === "new_notification") {
            // Refresh notifications and show new ones
            getNotifications().then(() => {
              // After getting updated notifications, check for new unread ones
              setTimeout(() => {
                const newUnreadNotifications = notificationList.filter(
                  (notification) => !notification.isRead
                );

                if (newUnreadNotifications.length > 0) {
                  // Show the newest unread notification
                  const newestUnread = newUnreadNotifications[0];
                  showNotificationToast(newestUnread);
                }
              }, 100);
            });
          }
        } catch (error) {
          console.error("Error parsing socket message:", error);
        }
      };
    }
  }, [isConnected, getNotifications, showNotificationToast, notificationList]);

  return (
    <>
      <NotificationToastContainer
        onNotificationClick={handleNotificationClick}
      />
      <header className="lg:sticky top-0 flex w-full max-h-[75px] bg-[#EDF6F9] shadow-sm z-40">
        <div className="flex flex-grow items-center justify-between py-4 px-4 shadow-2 md:px-6 2xl:px-11">
          {/* Mobile hamburger */}
          <div
            className="hover:cursor-pointer block md:hidden pt-2 md:w-full"
            onClick={handleMobSideBar}
          >
            <Hamburger />
          </div>

          {/* Back button */}
          {showBackButton && (
            <button
              onClick={handleBackNavigation}
              className="flex p-3 justify-center items-center gap-3 lg:w-[86px] lg:mx-0 mx-3 lg:h-[45px] w-[50px] h-[35px] bg-bluishgrey bg-opacity-[60%] shadow-card rounded-15 hover:scale-105 transition-transform"
            >
              <svg
                width="9"
                height="16"
                viewBox="0 0 9 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.337232 8.60567C0.121631 8.38689 0.000513077 8.09019 0.000513077 7.78084C0.000513077 7.47148 0.121631 7.17479 0.337232 6.956L6.84305 0.35617C6.94914 0.244741 7.07604 0.155862 7.21635 0.094718C7.35666 0.0335742 7.50757 0.00139017 7.66027 4.40491e-05C7.81297 -0.00130207 7.96441 0.0282169 8.10575 0.0868779C8.24708 0.145539 8.37549 0.232168 8.48347 0.341709C8.59145 0.451251 8.67685 0.581511 8.73467 0.72489C8.7925 0.868269 8.8216 1.0219 8.82027 1.1768C8.81894 1.33171 8.78722 1.4848 8.72694 1.62714C8.66667 1.76948 8.57906 1.89822 8.46922 2.00584L2.77648 7.78084L8.46922 13.5558C8.67871 13.7759 8.79463 14.0706 8.79201 14.3765C8.78939 14.6824 8.66844 14.975 8.45521 15.1913C8.24198 15.4076 7.95353 15.5303 7.65199 15.533C7.35046 15.5356 7.05995 15.418 6.84305 15.2055L0.337232 8.60567Z"
                  fill="#DE4AC4"
                />
              </svg>
              <p className="text-bodyRB text-darkgrey hidden lg:block">Back</p>
            </button>
          )}

          {/* Window title */}
          <h1 className="w-full flex justify-center md:ml-[15%] text-headingBB text-darkgrey capitalize">
            {formattedPathname || "dashboard"}
          </h1>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Notification bell */}
            <div className="relative flex items-center space-x-2">
              <button
                ref={notificationIconRef}
                onClick={() => setNotificationPanel(!notificationPanel)}
                className={`hover:scale-105 transition-transform lg:text-[22px] text-[16px] lg:bg-bluishgrey bg-white lg:bg-opacity-[10%] w-[34px] h-[36px] mt-[3px] lg:mt-0 lg:w-[43px] lg:h-[43px] flex items-center justify-center rounded-lg lg:rounded-full shadow-card 
                ${
                  hasUnread
                    ? "text-danger animate-bell-ring origin-center"
                    : " text-pink"
                }
                  `}
              >
                <BiSolidBell className={hasUnread ? "animate-bounce" : ""} />
              </button>
              {hasUnread && (
                <div className="w-2 h-2 rounded-full bg-danger absolute right-0   lg:top-1 top-0">
                  {/* Ping animation */}
                  <span className="absolute inline-flex h-full w-full rounded-full bg-danger opacity-75 animate-ping"></span>
                </div>
              )}
              {/* Refresh button */}
              <button
                onClick={() => window.location.reload()}
                className={`ml-2 hover:scale-105 transition-transform  lg:text-[22px] text-[16px] text-pink bg-bluishgrey bg-opacity-[10%] w-[34px] h-[36px] mt-[3px] lg:mt-0 lg:w-[43px] lg:h-[43px] flex items-center justify-center rounded-lg lg:rounded-full shadow-card relative ${
                  notificationLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                aria-label="Refresh notifications"
                disabled={notificationLoading}
              >
                <IoMdRefresh strokeWidth={2.5} size={25} />
              </button>
            </div>

            {/* Profile button */}
            <button
              onClick={handleProfileToggle}
              className="hover:scale-105 transition-transform w-8 h-8 lg:w-[43px] lg:h-[43px]"
            >
              <div className="hidden md:flex lg:text-[18px] text-[16px] text-pink bg-bluishgrey bg-opacity-[10%] lg:w-[43px] lg:h-[43px] items-center justify-center rounded-full shadow-card">
                <FaUserLarge />
              </div>
              <div className="block md:hidden -mt-1">
                <ProfileIcon_mob />
              </div>
            </button>

            {/* Fullscreen button */}
            <button
              onClick={handleFullScreenToggle}
              className="cursor-pointer hover:scale-105 transition-transform lg:text-[24px] text-[16px] text-pink bg-bluishgrey bg-opacity-[10%] w-[22px] h-[22px] lg:w-[43px] lg:h-[43px] flex items-center justify-center rounded-full shadow-card hidden md:flex"
            >
              <RiFullscreenFill />
            </button>

            {/* Logout button */}
            <div className="relative" ref={logoutButtonRef}>
              <button
                className="hover:scale-105 transition-transform w-8 h-8 lg:w-[57px] lg:h-[53px] hidden md:block"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLogout(!showLogout);
                }}
              >
                <LogoutIcon />
              </button>

              {showLogout && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[150px] z-50">
                  <div className="text-center mb-3">
                    <div className="font-medium text-gray-800 truncate max-w-[120px]">
                      {currentUser.displayName}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center space-x-2 w-full py-2 px-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-md transition-colors"
                  >
                    <FiLogOut />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notification Panel */}
        <NotificationPanel
          isOpen={notificationPanel}
          onClose={() => setNotificationPanel(false)}
          notifications={notificationList}
          loading={notificationLoading}
          onNotificationClick={handleNotificationClick}
          onClearAll={handleClearAllUnread}
          hasUnread={hasUnread}
        />

        <ProfileModal />
        <PopupImages />
      </header>
    </>
  );
};

export default Header;
